<template>
  <div :class="['UIFaqs', `UIFaqs--theme-${theme}`]">
    <div
      v-for="(item, index) in faqs"
      :key="index"
      :class="['UIFaqs__item', { 'UIFaqs__item--open': isOpen(index) }]"
    >
      <button
        type="button"
        class="UIFaqs__question"
        :aria-expanded="isOpen(index)"
        @click="toggle(index)"
      >
        <span class="UIFaqs__question-text">{{ item.question }}</span>
        <UIIcon
          :key="isOpen(index) ? openedIcon : closedIcon"
          class="UIFaqs__icon"
          :name="isOpen(index) ? openedIcon : closedIcon"
        />
      </button>

      <transition
        :css="false"
        @before-enter="beforeEnter"
        @enter="enter"
        @after-enter="afterEnter"
        @before-leave="beforeLeave"
        @leave="leave"
        @after-leave="afterLeave"
      >
        <div v-show="isOpen(index)" class="UIFaqs__answer">
          <slot name="answer" :item="item" :index="index">
            <template v-if="renderFormat === 'markdown'">
              <div
                class="UIFaqs__answer-text"
                v-html="renderAnswer(item.answer)"
              ></div>
            </template>
            <template v-else>
              <p class="UIFaqs__answer-text">{{ item.answer }}</p>
            </template>
          </slot>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import UIIcon from '../../ui/icons/UIIcon.vue'
import type { IIcons } from '../../ui/icons/types'
import DOMPurify from 'isomorphic-dompurify'

type FaqItem = {
  question: string
  answer: string
}

const props = withDefaults(
  defineProps<{
    faqs: FaqItem[]
    allowMultiple?: boolean
    theme?: 'light' | 'neutral'
    /** icon name when item is opened (placeholder; user may replace later) */
    openedIcon?: IIcons
    /** icon name when item is closed (placeholder; user may replace later) */
    closedIcon?: IIcons
    defaultOpenIndexes?: number[]
    renderFormat?: 'plain' | 'markdown'
  }>(),
  {
    allowMultiple: true,
    theme: 'light',
    openedIcon: 'minusCircle',
    closedIcon: 'plusCircle',
    defaultOpenIndexes: () => [0],
    renderFormat: 'plain',
  },
)

const openIndexes = ref<number[]>([...props.defaultOpenIndexes])

const isOpen = (idx: number): boolean => openIndexes.value.includes(idx)

const toggle = (idx: number) => {
  const currentlyOpen = isOpen(idx)
  if (currentlyOpen) {
    openIndexes.value = openIndexes.value.filter((i) => i !== idx)
    return
  }

  if (props.allowMultiple) {
    openIndexes.value = [...openIndexes.value, idx]
  } else {
    openIndexes.value = [idx]
  }
}

// Smooth height + opacity transitions using JS hooks
const setTransition = (el: HTMLElement) => {
  el.style.transition = [
    'height 0.28s ease',
    'opacity 0.24s ease',
    'transform 0.24s ease',
    'padding-top 0.28s ease',
    'padding-bottom 0.28s ease',
    'border-top-width 0.28s ease',
  ].join(', ')
}

const beforeEnter = (el: Element) => {
  const e = el as HTMLElement
  const s = window.getComputedStyle(e)
  // store original paddings and border to restore during enter
  e.dataset.pt = s.paddingTop
  e.dataset.pb = s.paddingBottom
  e.dataset.btw = s.borderTopWidth
  // measure target height BEFORE collapsing paddings/border
  const targetHeight = e.scrollHeight
  e.dataset.th = String(targetHeight)

  setTransition(e)
  e.style.height = '0px'
  e.style.opacity = '0'
  e.style.transform = 'translateY(-4px)'
  e.style.paddingTop = '0px'
  e.style.paddingBottom = '0px'
  e.style.borderTopWidth = '0px'
  e.style.overflow = 'hidden'
}

const enter = (el: Element) => {
  const e = el as HTMLElement
  // force reflow
  void e.offsetHeight
  e.style.height = `${e.dataset.th || e.scrollHeight}px`
  e.style.opacity = '1'
  e.style.transform = 'translateY(0)'
  if (e.dataset.pt) e.style.paddingTop = e.dataset.pt
  if (e.dataset.pb) e.style.paddingBottom = e.dataset.pb
  if (e.dataset.btw) e.style.borderTopWidth = e.dataset.btw
}

const afterEnter = (el: Element) => {
  const e = el as HTMLElement
  e.style.transition = ''
  e.style.height = 'auto'
  e.style.transform = ''
  e.style.opacity = ''
  e.style.paddingTop = ''
  e.style.paddingBottom = ''
  e.style.borderTopWidth = ''
  e.style.overflow = ''
}

const beforeLeave = (el: Element) => {
  const e = el as HTMLElement
  setTransition(e)
  e.style.height = `${e.scrollHeight}px`
  e.style.opacity = '1'
  e.style.transform = 'translateY(0)'
  e.style.overflow = 'hidden'
  // force reflow to lock height before collapsing
  void e.offsetHeight
}

const leave = (el: Element) => {
  const e = el as HTMLElement
  e.style.height = '0px'
  e.style.opacity = '0'
  e.style.transform = 'translateY(-4px)'
  e.style.paddingTop = '0px'
  e.style.paddingBottom = '0px'
  e.style.borderTopWidth = '0px'
}

const afterLeave = (el: Element) => {
  const e = el as HTMLElement
  e.style.transition = ''
  e.style.height = ''
  e.style.transform = ''
  e.style.opacity = ''
  e.style.paddingTop = ''
  e.style.paddingBottom = ''
  e.style.borderTopWidth = ''
  e.style.overflow = ''
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

// Minimal markdown support for paragraphs and unordered lists.
// - Supports lines starting with "- " or "* " as list items
// - Empty lines separate paragraphs
const renderMarkdownBasic = (text: string): string => {
  const lines = (text || '').split(/\r?\n/)
  let html = ''
  let inList = false
  let paragraphBuffer: string[] = []

  const flushParagraph = () => {
    if (paragraphBuffer.length) {
      const paragraphText = escapeHtml(paragraphBuffer.join(' ')).trim()
      if (paragraphText) html += `<p>${paragraphText}</p>`
      paragraphBuffer = []
    }
  }

  for (const rawLine of lines) {
    const line = rawLine.trim()
    const listMatch = /^[-*]\s+(.+)$/.exec(line)

    if (listMatch) {
      flushParagraph()
      if (!inList) {
        html += '<ul>'
        inList = true
      }
      html += `<li>${escapeHtml(listMatch[1])}</li>`
      continue
    }

    if (line === '') {
      if (inList) {
        html += '</ul>'
        inList = false
      }
      flushParagraph()
      continue
    }

    if (inList) {
      html += '</ul>'
      inList = false
    }
    paragraphBuffer.push(rawLine)
  }

  if (inList) html += '</ul>'
  flushParagraph()
  return html
}

const renderAnswer = (text: string): string => {
  const rawHtml = renderMarkdownBasic(text)
  return DOMPurify.sanitize(rawHtml)
}
</script>

<style lang="scss" scoped>
.UIFaqs {
  --faq-radius: 1rem;
  --faq-spacing: 1rem;
  --faq-border: 1px solid $color-grey-300;
  --faq-bg: #{$color-white};
  --faq-question-bg: #{$color-ui-gray-cool-50};
  --faq-question-color: #{$color-ui-neutral-main};
  --faq-answer-color: #{$color-ui-neutral-700};

  &.UIFaqs--theme-neutral {
    --faq-question-bg: #{$color-white};
  }

  .UIFaqs__item {
    padding: 1rem;
    border: var(--faq-border);
    border-radius: var(--faq-radius);
    background-color: transparent;
    overflow: hidden;
    // Smoothly fade background when opening/closing
    @include trans(0.24s);
    transition-property: background-color, border-color;
    & + .UIFaqs__item {
      margin-top: var(--faq-spacing);
    }
    &.UIFaqs__item--open {
      // Make the entire block (question + answer) share the same grey background
      background-color: var(--faq-question-bg);
      .UIFaqs__question {
        background-color: transparent;
      }
      .UIFaqs__answer {
        background-color: transparent;
      }
    }
  }

  .UIFaqs__question {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    text-align: left;
    background-color: transparent;
    color: var(--faq-question-color);
    padding: 0rem 0.5rem 0rem 0.5rem;
    cursor: pointer;
    border: 0;
  }

  .UIFaqs__question-text {
    font-weight: 600;
  }

  .UIFaqs__icon {
    --size: 1.25rem;
    flex-shrink: 0;
  }

  .UIFaqs__answer {
    padding: 0.5rem 0.5rem 0.5rem 0.5rem;
    color: var(--faq-answer-color);
    border-top: var(--faq-border);
    overflow: hidden; // needed for height animation
    // ensure slight content fade while height expands
    @include trans(0.24s);
    transition-property: opacity, transform;
    will-change: height, opacity, transform;
  }

  // Markdown content defaults
  .UIFaqs__answer-text {
    :deep(ul),
    :deep(ol) {
      margin: 0.5rem 0;
      padding-left: 1.25rem;
    }
    :deep(ul) {
      list-style: disc;
      list-style-position: outside;
    }
    :deep(ol) {
      list-style: decimal;
      list-style-position: outside;
    }
    :deep(li) {
      margin: 0.25rem 0;
    }
  }
}
</style>
